import React, { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { Badge, LoadError, Loading, Row, Welcome, dayLabel, longDate, money, propName, useLoad, usePortal } from '../../components/clientportal/kit';
import { ClientStripeCardForm } from '../../components/StripeCardForm';

const statement = (i, name, prop) => {
  const text = ['GLEAM GURU CLEANERS', 'STATEMENT', i.number, name, prop, `Service: ${i.service}`, `Service date: ${longDate(i.service_date)}`,
    `Invoice total: ${money(i.total_cents)}`, `Paid: ${money(i.paid_cents)}`, `Remaining: ${money(i.remaining_cents)}`, `Due: ${longDate(i.due_date)}`].join('\n');
  const url = URL.createObjectURL(new Blob([text], { type: 'text/plain' }));
  const a = document.createElement('a'); a.href = url; a.download = `${i.number}-statement.txt`; a.click();
  setTimeout(() => URL.revokeObjectURL(url), 1000);
};

const InvoiceDetail = ({ i, propLabel }) => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const { closeDrawer } = usePortal();
  return (
    <div data-testid="invoice-detail">
      <span className="eyebrow">{user?.full_name} · {propLabel}</span>
      <div className="amount">{money(i.total_cents)}</div><Badge>{i.status}</Badge>
      <Row k="Service" v={i.service} /><Row k="Service date" v={longDate(i.service_date)} /><Row k="Due date" v={longDate(i.due_date)} />
      <Row k="Invoice total" v={money(i.total_cents)} /><Row k="Paid" v={money(i.paid_cents)} /><Row k="Remaining" v={money(i.remaining_cents)} testId="invoice-remaining" />
      {i.remaining_cents > 0 ? (i.pay_url
        ? <><div className="notice">Payment is handled on our secure payment page. Your balance updates once the payment is confirmed.</div><a className="primary" href={i.pay_url} target="_blank" rel="noreferrer" data-testid="invoice-pay">Pay securely ↗</a></>
        : <div className="notice warm">Your team charges your card on file or confirms payment arrangements with you. Nothing is charged from this page.</div>)
        : <div className="notice">Payment recorded{i.paid_at ? ` on ${longDate(i.paid_at)}` : ''}. Thank you.</div>}
      <div className="inline-actions">
        <button className="text-button" onClick={() => statement(i, user?.full_name, propLabel)} data-testid="invoice-download">Download statement ↓</button>
        {i.pdf_url && <a className="text-button" href={i.pdf_url} target="_blank" rel="noreferrer">Invoice PDF ↗</a>}
        <button className="text-button" onClick={() => { closeDrawer(); navigate('/portal/messages'); }}>Ask about this invoice ↗</button>
      </div>
    </div>
  );
};

const CardPanel = ({ hasCard, onSaved }) => {
  const { token, user } = useAuth();
  const { openDrawer, closeDrawer, toast } = usePortal();
  const add = () => openDrawer({ title: 'Payment method', label: 'SECURE PAYMENT', content: <ClientStripeCardForm token={token} cardholderName={user?.full_name}
    onSuccess={() => { closeDrawer(); toast('Payment method saved.'); onSaved(); }} onCancel={closeDrawer} /> });
  return (
    <section className="panel" data-testid="billing-payment-details"><span className="eyebrow">PAYMENT DETAILS</span>
      <h2 style={{ marginTop: 16 }}>{hasCard ? 'Card on file.' : 'Secure payment.'}</h2>
      <p>{hasCard ? 'A saved card is on file with our payment provider. Card details are never stored by Gleam Guru.' : 'No saved payment method. Card details are handled by our secure payment provider, never stored by Gleam Guru.'}</p>
      <button className="outline" onClick={add} data-testid="billing-payment-method">{hasCard ? 'Replace card ↗' : 'Add a payment method ↗'}</button>
    </section>
  );
};

export default function ClientBillingPage() {
  const { openDrawer } = usePortal();
  const [params] = useSearchParams();
  const [b, reload, err] = useLoad('/api/portal/billing');
  const [props] = useLoad('/api/clients/me/addresses');
  const [tab, setTab] = useState('Invoices');
  const propLabel = (i) => i.property_label || propName((props || []).find((p) => p.id === i.address_id));
  const open = (i) => openDrawer({ title: i.number, label: 'INVOICE', content: <InvoiceDetail i={i} propLabel={propLabel(i)} /> });
  useEffect(() => { const i = (b?.invoices || []).find((x) => x.id === params.get('invoice')); if (i && props) open(i); }, [b, props]); // eslint-disable-line react-hooks/exhaustive-deps
  const head = <Welcome title="Billing" desc="Your agreed prices, invoices, and payment history." />;
  if (err && !b) return <>{head}<LoadError text={err} onRetry={reload} /></>;
  if (!b) return <>{head}<Loading /></>;
  const due = b.invoices.filter((i) => i.remaining_cents > 0).sort((x, y) => (x.due_date || '').localeCompare(y.due_date || ''))[0];
  const rows = b.invoices.filter((i) => tab === 'Invoices' || i.paid_cents > 0);
  return (
    <div data-testid="client-billing">
      {head}
      <div className="two-col" style={{ margin: '0 0 23px' }}>
        <section className="panel"><span className="eyebrow">CURRENT BALANCE</span><div className="amount" data-testid="billing-balance">{money(b.balance_cents)}</div>
          {due ? <><p>Invoice {due.number}{due.due_date ? ` · Due ${longDate(due.due_date)}` : ''}</p><button className="text-button" onClick={() => open(due)} data-testid="billing-review-due">Review invoice ↗</button></>
            : <p>You’re all paid up.</p>}</section>
        <CardPanel hasCard={b.has_card_on_file} onSaved={reload} />
      </div>
      <div className="tabs" role="group" aria-label="Billing views">
        {['Invoices', 'Payment history'].map((t) => <button key={t} className={t === tab ? 'active' : ''} aria-pressed={t === tab} onClick={() => setTab(t)} data-testid={`billing-tab-${t.split(' ')[0].toLowerCase()}`}>{t}</button>)}
      </div>
      <section className="panel">
        {rows.length ? <div className="table-scroll"><table data-testid="billing-table"><thead><tr><th>INVOICE</th><th>PROPERTY</th><th>{tab === 'Invoices' ? 'DUE DATE' : 'PAYMENT DATE'}</th><th>AMOUNT</th><th>STATUS</th><th /></tr></thead>
          <tbody>{rows.map((i) => <tr key={i.id} data-testid={`invoice-row-${i.id}`}><td><strong>{i.number}</strong><small>{i.service}</small></td><td>{propLabel(i)}</td>
            <td>{dayLabel(tab === 'Invoices' ? i.due_date || i.issued_at : i.paid_at)}</td><td>{money(tab === 'Invoices' ? i.total_cents : i.paid_cents)}</td><td><Badge>{i.status}</Badge></td>
            <td><button className="text-button" onClick={() => open(i)} data-testid={`invoice-view-${i.id}`}>View ↗</button></td></tr>)}</tbody></table></div>
          : <p className="empty" data-testid="billing-empty">{tab === 'Invoices' ? 'No invoices yet. Invoices for your cleans will appear here.' : 'No payments recorded yet.'}</p>}
      </section>
      <p className="muted">Amounts are the prices agreed with your team. Questions about a charge? Message your team.</p>
    </div>
  );
}
